import { action, computed, setProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { ModelFrom } from '../../../../customer-site/app/utils/type-utils';
import { zonedTimeToUtc } from 'date-fns-tz';
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';

import type { Registry as ServiceRegistry } from '@ember/service';
import type Ajax from 'unattended-showing/services/ajax';
import type Appointment from 'unattended-showing/models/appointment';
import type LocationSgt3Route from './route';
import type SGTSession from 'unattended-showing/services/sgt-session';

export default class LocationController extends Controller {
    @service declare sgtSession: SGTSession;
    @service declare ajax: Ajax;
    @service declare router: ServiceRegistry['router'];
    @service declare store: ServiceRegistry['store'];
    @service
    declare notificationManager: ServiceRegistry['notification-manager'];

    declare model: ModelFrom<LocationSgt3Route>;

    @tracked showReviewAndBook: boolean = false;
    @tracked appointment: Appointment | null = null;
    @tracked targetMoveInDate: Date | null =
        this.model.profile?.targetMoveInDate ?? null;

    /**
     * Settings for appointment-schedule component.
     */
    @computed('model.context')
    get appointmentSettings() {
        const { appointmentLength, hoursOfOperationSchedule } =
            this.model.context || {};
        return {
            appointmentLength,
            hoursOfOperationSchedule
        };
    }

    /**
     * Creates a new appointment record.
     */
    createNewAppointment(): Promise<Appointment> {
        return (async () => {
            const location = await this.model.location;
            const newAppointment = this.store.createRecord('appointment', {
                context: this.model.context,
                location: location
            });
            return newAppointment;
        })();
    }

    /**
     * Actions to perform when the user clicks the Back button on the Book a Tour screen.
     */
    @action bookTourBackAction(): void {
        // TODO POINT-6907
        return;
    }

    /**
     * Steps to take when the user clicks Next on the Book a Tour screen.
     */
    @action async bookTourNextAction(
        tourDateTime: Date,
        desiredMoveInDate: Date
    ): Promise<void> {
        const newAppointment: Appointment = await this.createNewAppointment(),
            location = await this.model.location,
            context = this.store.peekAll('context').objectAt(0);

        if (!location || !context) {
            this.showDefaultError();
            return;
        }

        const appointmentUtcDate = zonedTimeToUtc(
                tourDateTime,
                location.timeZone
            ),
            activePendingIdVerification = location.reverificationRequired,
            geofenceEnabled = location.geofenceEnabled;

        setProperties(newAppointment, {
            dateTimeUtc: appointmentUtcDate,
            current: true,
            past: false,
            activePendingIdVerification: activePendingIdVerification,
            showCheckIn: geofenceEnabled,
            subdomain: context.id
        });

        // Redirect to the login route if user is not logged in.
        const profileId = this.sgtSession.data?.authenticated?.profileId;
        if (!this.sgtSession.isAuthenticated || !profileId) {
            this.router.transitionTo('context.login-sgt3');
            return;
        }

        // Redirect to credit card verification if user has not verified credit card
        // and the dealer requires that the credit card be verified.
        const profile = await this.store.findRecord('profile', profileId);
        if (context.showCCPage && !profile.creditCardValidated) {
            this.router.transitionTo(
                'context.sign-up-sgt3.credit-card-verification'
            );
            return;
        }

        this.appointment = newAppointment;
        this.targetMoveInDate = desiredMoveInDate;
        this.showReviewAndBook = true;
        return;
    }

    /**
     * Actions to perform when the user clicks the back button on the Review & Book screen.
     */
    @action reviewAndBookBackAction(): void {
        this.showReviewAndBook = false;
        return;
    }

    /**
     * Actions to perform when the user clicks the Book Tour button on the Review & Book screen.
     */
    @action async reviewAndBookNextAction(): Promise<void> {
        console.log('this was called');
        const location = await this.model.location;
        if (!this.appointment) {
            this.showDefaultError();
            return;
        }
        try {
            await this.appointment.save();
            // TODO POINT-4774: Save the desired move in date.
            if (location) {
                location.reload();
                window.scrollTo(0, 0);
            }
        } catch (response) {
            return this.ajax.handleAjaxError(
                response,
                'Unable to request Self-Guided Tours appointment. Please try again later.'
            );
        }
    }

    /**
     * Display a generic error through the notification manager when an action cannot be taken.
     */
    showDefaultError(): void {
        const defaultErrorMsg =
            'Unable to request Self-Guided Tours appointment. Please try again later.';
        this.notificationManager.addError(defaultErrorMsg);
    }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
    interface Registry {
        'context/location-sgt3': LocationController;
    }
}
